// src/components/tags/TagList.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchTags,
  deleteTag,
  clearTagError,
  clearTagSuccess,
  setTagFilters,
  mergeTags,
} from '../../store/actions/tagActions';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import FilterBar from '../common/FilterBar';

const TagList = () => {
  const dispatch = useDispatch();
  const { tags, loading, error, success, meta, filters } = useSelector(
    (state) => state.tags
  );

  const [selectedTag, setSelectedTag] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [sourceTag, setSourceTag] = useState(null);
  const [targetTag, setTargetTag] = useState(null);

  const initialMount = useRef(true);
  const filtersRef = useRef(filters);

  const typeOptions = [
    { value: '', label: 'Tous les types' },
    { value: 'GENERAL', label: 'Général' },
    { value: 'FAQ', label: 'FAQ' },
    { value: 'BLOG', label: 'Blog' },
    { value: 'TOPIC', label: 'Sujet' },
    { value: 'SKILL', label: 'Compétence' },
  ];

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
    },
    createButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    createButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    mergeButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#8b5cf6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    mergeButtonHover: {
      backgroundColor: '#7c3aed',
    },
    mergeButtonDisabled: {
      backgroundColor: '#e5e7eb',
      color: '#9ca3af',
      cursor: 'not-allowed',
    },
    bulkDeleteButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#dc2626',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    bulkDeleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    bulkDeleteButtonDisabled: {
      backgroundColor: '#e5e7eb',
      color: '#9ca3af',
      cursor: 'not-allowed',
    },
    filterBar: {
      marginBottom: '1rem',
    },
    tableContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    tableHeader: {
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCell: {
      padding: '0.75rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCellCheckbox: {
      padding: '0.75rem 1.5rem',
      width: '40px',
    },
    tableHeaderCellRight: {
      padding: '0.75rem 1.5rem',
      textAlign: 'right',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
      transition: 'background-color 0.2s',
    },
    tableRowHover: {
      backgroundColor: '#f9fafb',
    },
    tableCell: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
    },
    tableCellCheckbox: {
      padding: '1rem 1.5rem',
      width: '40px',
    },
    tableCellRight: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
      textAlign: 'right',
    },
    tagInfo: {
      display: 'flex',
      flexDirection: 'column',
    },
    tagName: {
      fontWeight: 600,
      color: '#111827',
      textDecoration: 'none',
    },
    tagSlug: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    typeBadge: {
      backgroundColor: '#f3f4f6',
      color: '#374151',
    },
    activeBadge: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    inactiveBadge: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    usageCount: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    actionsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '0.5rem',
    },
    actionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.25rem',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'color 0.2s',
      borderRadius: '0.375rem',
    },
    viewButton: {
      color: '#2563eb',
    },
    viewButtonHover: {
      color: '#1d4ed8',
    },
    editButton: {
      color: '#4f46e5',
    },
    editButtonHover: {
      color: '#4338ca',
    },
    mergeSourceButton: {
      color: '#8b5cf6',
    },
    mergeSourceButtonHover: {
      color: '#7c3aed',
    },
    deleteButton: {
      color: '#dc2626',
    },
    deleteButtonHover: {
      color: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
    mergeModal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    mergeModalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      padding: '2rem',
      maxWidth: '500px',
      width: '90%',
    },
    mergeModalTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    mergeInfo: {
      padding: '1rem',
      backgroundColor: '#f3f4f6',
      borderRadius: '0.5rem',
      marginBottom: '1.5rem',
    },
    mergeTag: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.375rem',
      marginBottom: '0.5rem',
    },
    mergeArrow: {
      textAlign: 'center',
      fontSize: '1.25rem',
      color: '#6b7280',
      margin: '0.5rem 0',
    },
    select: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      marginBottom: '1rem',
    },
    modalButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
    saveButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#8b5cf6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
  };

  const fetchTagsData = useCallback(() => {
    dispatch(fetchTags(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      fetchTagsData();
    } else {
      if (JSON.stringify(filtersRef.current) !== JSON.stringify(filters)) {
        filtersRef.current = filters;
        fetchTagsData();
      }
    }
  }, [fetchTagsData, filters]);

  useEffect(() => {
    return () => {
      dispatch(clearTagError());
      dispatch(clearTagSuccess());
    };
  }, [dispatch]);

  const handleSearch = useCallback((searchTerm) => {
    dispatch(setTagFilters({
      ...filters,
      search: searchTerm,
      page: 1,
    }));
  }, [dispatch, filters]);

  const handleFilterChange = useCallback((newFilters) => {
    dispatch(setTagFilters({
      ...filters,
      ...newFilters,
      page: 1,
    }));
  }, [dispatch, filters]);

  const handlePageChange = useCallback((page) => {
    dispatch(setTagFilters({
      ...filters,
      page,
    }));
  }, [dispatch, filters]);

  const handleDeleteClick = useCallback((tag) => {
    setSelectedTag(tag);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await dispatch(deleteTag(selectedTag.id));
      setShowDeleteConfirm(false);
      setSelectedTag(null);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, selectedTag]);

  const handleCloseDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setSelectedTag(null);
  }, []);

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) {
      setSelectedIds(tags.map(tag => tag.id));
    } else {
      setSelectedIds([]);
    }
  }, [tags]);

  const handleSelectOne = useCallback((id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      } else {
        return [...prev, id];
      }
    });
  }, []);

  const handleBulkDeleteClick = useCallback(() => {
    if (selectedIds.length > 0) {
      setShowBulkDeleteConfirm(true);
    }
  }, [selectedIds]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    try {
      // await dispatch(bulkDeleteTags(selectedIds));
      setShowBulkDeleteConfirm(false);
      setSelectedIds([]);
    } catch (error) {
      console.error('Bulk delete failed:', error);
    }
  }, [dispatch, selectedIds]);

  const handleMergeSource = (tag) => {
    setSourceTag(tag);
    setTargetTag(null);
    setShowMergeModal(true);
  };

  const handleMergeConfirm = async () => {
    if (sourceTag && targetTag && sourceTag.id !== targetTag.id) {
      try {
        await dispatch(mergeTags(sourceTag.id, targetTag.id));
        setShowMergeModal(false);
        setSourceTag(null);
        setTargetTag(null);
      } catch (error) {
        console.error('Merge failed:', error);
      }
    }
  };

  if (loading && !tags.length) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Gestion des tags</h1>
        <div style={styles.headerActions}>
          {selectedIds.length === 2 && (
            <button
              onClick={() => {
                const [first, second] = selectedIds;
                const tag1 = tags.find(t => t.id === first);
                const tag2 = tags.find(t => t.id === second);
                setSourceTag(tag1);
                setTargetTag(tag2);
                setShowMergeModal(true);
              }}
              style={styles.mergeButton}
            >
              <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              Fusionner les 2 tags
            </button>
          )}
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDeleteClick}
              style={{
                ...styles.bulkDeleteButton,
                ...(selectedIds.length === 0 ? styles.bulkDeleteButtonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (selectedIds.length > 0) {
                  e.currentTarget.style.backgroundColor = styles.bulkDeleteButtonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (selectedIds.length > 0) {
                  e.currentTarget.style.backgroundColor = styles.bulkDeleteButton.backgroundColor;
                }
              }}
            >
              <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Supprimer ({selectedIds.length})
            </button>
          )}
          <Link
            to="/admin/tags/form"
            style={styles.createButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.createButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.createButton.backgroundColor;
            }}
          >
            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Nouveau tag
          </Link>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearTagError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearTagSuccess())}
        />
      )}

      <div style={styles.filterBar}>
        <FilterBar
          filters={[
            {
              type: 'select',
              name: 'type',
              label: 'Type',
              options: typeOptions,
            },
            {
              type: 'select',
              name: 'isActive',
              label: 'Statut',
              options: [
                { value: '', label: 'Tous' },
                { value: 'true', label: 'Actif' },
                { value: 'false', label: 'Inactif' },
              ],
            },
          ]}
          onFilterChange={handleFilterChange}
          initialValues={filters}
        />
      </div>

      <div style={styles.searchContainer}>
        <SearchBar 
          onSearch={handleSearch} 
          placeholder="Rechercher des tags par nom, slug ou description..." 
          fullWidth
        />
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCellCheckbox}>
                <input
                  type="checkbox"
                  checked={selectedIds.length === tags.length && tags.length > 0}
                  onChange={handleSelectAll}
                  style={styles.checkbox}
                />
              </th>
              <th style={styles.tableHeaderCell}>Tag</th>
              <th style={styles.tableHeaderCell}>Type</th>
              <th style={styles.tableHeaderCell}>Statut</th>
              <th style={styles.tableHeaderCell}>Utilisation</th>
              <th style={styles.tableHeaderCellRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tags.map((tag) => (
              <tr 
                key={tag.id} 
                style={styles.tableRow}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={styles.tableCellCheckbox}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(tag.id)}
                    onChange={() => handleSelectOne(tag.id)}
                    style={styles.checkbox}
                  />
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.tagInfo}>
                    <Link to={`/admin/tags/${tag.id}/details`} style={styles.tagName}>
                      {tag.name}
                    </Link>
                    <div style={styles.tagSlug}>/{tag.slug}</div>
                    {tag.description && (
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        {tag.description.substring(0, 50)}...
                      </div>
                    )}
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <span style={{ ...styles.badge, ...styles.typeBadge }}>
                    {typeOptions.find(t => t.value === tag.type)?.label || tag.type}
                  </span>
                </td>
                <td style={styles.tableCell}>
                  <span
                    style={{
                      ...styles.badge,
                      ...(tag.isActive ? styles.activeBadge : styles.inactiveBadge),
                    }}
                  >
                    {tag.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.usageCount}>
                    <div>📄 {tag._count?.faqs || 0} FAQs</div>
                    <div>📰 {tag._count?.blogPosts || 0} articles</div>
                    <div>📎 {tag._count?.documents || 0} documents</div>
                  </div>
                </td>
                <td style={styles.tableCellRight}>
                  <div style={styles.actionsContainer}>
                    <Link
                      to={`/admin/tags/${tag.id}/details`}
                      style={{
                        ...styles.actionButton,
                        ...styles.viewButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.viewButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.viewButton.color;
                      }}
                      title="Voir les détails"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </Link>

                    <Link
                      to={`/admin/tags/${tag.id}/edit`}
                      style={{
                        ...styles.actionButton,
                        ...styles.editButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.editButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.editButton.color;
                      }}
                      title="Modifier"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>

                    <button
                      onClick={() => handleMergeSource(tag)}
                      style={{
                        ...styles.actionButton,
                        ...styles.mergeSourceButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.mergeSourceButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.mergeSourceButton.color;
                      }}
                      title="Fusionner"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                    </button>

                    <button
                      onClick={() => handleDeleteClick(tag)}
                      style={{
                        ...styles.actionButton,
                        ...styles.deleteButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.deleteButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.deleteButton.color;
                      }}
                      title="Supprimer"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {tags.length === 0 && !loading && (
          <div style={styles.emptyState}>
            Aucun tag trouvé correspondant à vos critères
          </div>
        )}

        <Pagination
          currentPage={meta.page}
          totalPages={meta.totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Merge Modal */}
      {showMergeModal && (
        <div style={styles.mergeModal} onClick={() => setShowMergeModal(false)}>
          <div style={styles.mergeModalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.mergeModalTitle}>Fusionner des tags</h3>
            
            <div style={styles.mergeInfo}>
              <div style={styles.mergeTag}>
                <strong>Source:</strong> {sourceTag?.name}
                <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                  (sera fusionné)
                </span>
              </div>
              <div style={styles.mergeArrow}>↓</div>
              <div style={styles.mergeTag}>
                <strong>Cible:</strong>
                <select
                  style={styles.select}
                  value={targetTag?.id || ''}
                  onChange={(e) => setTargetTag(tags.find(t => t.id === e.target.value))}
                >
                  <option value="">Sélectionner un tag cible</option>
                  {tags
                    .filter(t => t.id !== sourceTag?.id)
                    .map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.type})
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div style={styles.modalButtons}>
              <button
                onClick={() => setShowMergeModal(false)}
                style={styles.cancelButton}
              >
                Annuler
              </button>
              <button
                onClick={handleMergeConfirm}
                disabled={!targetTag || sourceTag?.id === targetTag?.id}
                style={{
                  ...styles.saveButton,
                  opacity: !targetTag || sourceTag?.id === targetTag?.id ? 0.5 : 1,
                  cursor: !targetTag || sourceTag?.id === targetTag?.id ? 'not-allowed' : 'pointer',
                }}
              >
                Fusionner
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={handleCloseDelete}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le tag"
        message={`Êtes-vous sûr de vouloir supprimer le tag "${selectedTag?.name}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmDialog
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={handleBulkDeleteConfirm}
        title="Supprimer plusieurs tags"
        message={`Êtes-vous sûr de vouloir supprimer ${selectedIds.length} tag(s) ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default TagList;